import { useMemo, useState, useCallback } from 'react';

export type ProgressiveSkeletonResult = {
    completeStep: (step: string) => void;
    resetStep: (step: string) => void;
    resetAllSteps: () => void;
    isStepLoading: (step: string) => boolean;
    isStepCompleted: (step: string) => boolean;
    completedSteps: Set<string>;
    progress: number;
};

export type ProgressiveSkeleton = {
    steps: string[];
};

/**
 * Hook pour gérer un chargement progressif par étapes
 * Utile pour des interfaces complexes qui se chargent en plusieurs phases
 *
 * @param params - Configuration du skeleton progressif
 * @param params.steps - Array des noms d'étapes à gérer
 * @returns Objet avec les contrôles et l'état du chargement progressif
 *
 * @example
 * ```tsx
 * const { completeStep, isStepLoading, progress, resetAllSteps } = useProgressiveSkeleton({
 *   steps: ['user', 'permissions', 'preferences', 'data']
 * });
 *
 * // Dans vos effects ou callbacks
 * useEffect(() => {
 *   fetchUser().then(() => completeStep('user'));
 *   fetchPermissions().then(() => completeStep('permissions'));
 * }, []);
 *
 * return (
 *   <div>
 *     <ProgressBar value={progress} />
 *     {isStepLoading('user') && <UserSkeleton />}
 *     {isStepLoading('permissions') && <PermissionsSkeleton />}
 *     <button onClick={resetAllSteps}>Restart</button>
 *   </div>
 * );
 * ```
 */
export const useProgressiveSkeleton = ({
    steps,
}: ProgressiveSkeleton): ProgressiveSkeletonResult => {
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

    const completeStep = useCallback((step: string) => {
        setCompletedSteps((prev) => {
            if (prev.has(step)) return prev; // Éviter les re-renders inutiles
            const newSet = new Set(prev);
            newSet.add(step);
            console.debug(`✅ [ProgressiveSkeleton] Step completed: ${step}`);
            return newSet;
        });
    }, []);

    const resetStep = useCallback((step: string) => {
        setCompletedSteps((prev) => {
            if (!prev.has(step)) return prev; // Éviter les re-renders inutiles
            const newSet = new Set(prev);
            newSet.delete(step);
            console.debug(`🔄 [ProgressiveSkeleton] Step reset: ${step}`);
            return newSet;
        });
    }, []);

    const resetAllSteps = useCallback(() => {
        setCompletedSteps(new Set());
        console.debug('🔄 [ProgressiveSkeleton] All steps reset');
    }, []);

    const result = useMemo(() => {
        const progress =
            steps.length === 0 ? 100 : Math.round((completedSteps.size / steps.length) * 100);

        return {
            completeStep,
            resetStep,
            resetAllSteps,
            isStepLoading: (step: string) => !completedSteps.has(step),
            isStepCompleted: (step: string) => completedSteps.has(step),
            completedSteps,
            progress,
        };
    }, [completedSteps, steps, completeStep, resetStep, resetAllSteps]);

    return result;
};
